import { computed, ref } from 'vue';
import type {
  IVatTuDinhMucCoDinh,
  IVatTuDinhMucCoDinhResponseBase,
  IVatTuResponseBase,
} from '@/core/interfaces/ApiResponses';
import type { DinhMucCoDinhVatTuLineDraft } from '@/types/dinh-muc-co-dinh';

let draftKeySeq = 0;

function nextDraftKey() {
  draftKeySeq += 1;

  return `dmcd-vt-${Date.now()}-${draftKeySeq}`;
}

export function createVatTuLine(input: {
  id_vat_tu: number;
  so_luong: number;
  ma_vat_tu?: string;
  ten_vat_tu?: string;
  don_vi_tinh?: string;
}): DinhMucCoDinhVatTuLineDraft {
  return {
    _key: nextDraftKey(),
    id_vat_tu: input.id_vat_tu,
    ma_vat_tu: input.ma_vat_tu,
    ten_vat_tu: input.ten_vat_tu,
    don_vi_tinh: input.don_vi_tinh,
    so_luong: input.so_luong,
  };
}

export function mapVatTuResponseToDraft(
  vatTu: IVatTuResponseBase & { so_luong?: number }
): DinhMucCoDinhVatTuLineDraft {
  return createVatTuLine({
    id_vat_tu: vatTu.id,
    so_luong: vatTu.so_luong ?? 1,
    ma_vat_tu: vatTu.ma_vat_tu,
    ten_vat_tu: vatTu.ten_vat_tu,
    don_vi_tinh: vatTu.don_vi_tinh,
  });
}

export function mapVatTuDinhMucPivotToDraft(
  item: IVatTuDinhMucCoDinh | IVatTuDinhMucCoDinhResponseBase
): DinhMucCoDinhVatTuLineDraft {
  const so_luong =
    'pivot' in item && item.pivot?.so_luong != null
      ? item.pivot.so_luong
      : 'so_luong_dinh_muc' in item
        ? item.so_luong_dinh_muc ?? 0
        : 0;

  return createVatTuLine({
    id_vat_tu: item.id,
    so_luong,
    ma_vat_tu: item.ma_vat_tu,
    ten_vat_tu: item.ten_vat_tu,
    don_vi_tinh: item.don_vi_tinh,
  });
}

export function mapVatTuDinhMucListToDraft(
  item: IVatTuDinhMucCoDinhResponseBase
): DinhMucCoDinhVatTuLineDraft {
  return createVatTuLine({
    id_vat_tu: item.id,
    so_luong: item.so_luong_dinh_muc ?? 0,
    ma_vat_tu: item.ma_vat_tu,
    ten_vat_tu: item.ten_vat_tu,
    don_vi_tinh: item.don_vi_tinh,
  });
}

export function useDinhMucCoDinhVatTuLines() {
  const lines = ref<DinhMucCoDinhVatTuLineDraft[]>([]);

  const existingIdSet = computed(() => {
    const ids = new Set<number>();

    lines.value.forEach((line) => {
      if (line.id_vat_tu) {
        ids.add(line.id_vat_tu);
      }
    });

    return ids;
  });

  const setLines = (next: DinhMucCoDinhVatTuLineDraft[]) => {
    lines.value = next;
  };

  const clearLines = () => {
    lines.value = [];
  };

  const hydrateFromVatTuDinhMuc = (
    items: Array<IVatTuDinhMucCoDinh | IVatTuDinhMucCoDinhResponseBase>
  ) => {
    lines.value = items.map(mapVatTuDinhMucPivotToDraft);
  };

  const hydrateFromVatTuDinhMucList = (
    items: IVatTuDinhMucCoDinhResponseBase[]
  ) => {
    lines.value = items.map(mapVatTuDinhMucListToDraft);
  };

  const addExistingFromVatTu = (
    items: Array<IVatTuResponseBase & { so_luong?: number }>
  ) => {
    const next: DinhMucCoDinhVatTuLineDraft[] = [...lines.value];

    items.forEach((item) => {
      if (existingIdSet.value.has(item.id)) {
        const index = next.findIndex((line) => line.id_vat_tu === item.id);

        if (index >= 0) {
          next[index] = {
            ...next[index],
            so_luong: item.so_luong ?? next[index].so_luong,
          };
        }

        return;
      }

      next.push(mapVatTuResponseToDraft(item));
    });

    lines.value = next;
  };

  const removeLine = (key: string) => {
    lines.value = lines.value.filter((line) => line._key !== key);
  };

  const patchLine = (
    key: string,
    patch: Partial<Omit<DinhMucCoDinhVatTuLineDraft, '_key'>>
  ) => {
    lines.value = lines.value.map((line) =>
      line._key === key ? { ...line, ...patch } : line
    );
  };

  return {
    lines,
    existingIdSet,
    setLines,
    clearLines,
    hydrateFromVatTuDinhMuc,
    hydrateFromVatTuDinhMucList,
    addExistingFromVatTu,
    removeLine,
    patchLine,
  };
}
